import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Actions_, Builder_, Checkbox_, Dropdown_, FormBox, FormBoxTitle, Grey_, Grey_Link, Input_, Main_, Main_Plain, Radio_, Red_, Textarea_, Toggle_ } from 'monica-alexandria';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import fields from '../fields.json';
import { service_catalog_items } from '../../../../utils/offers/service_catalog';
import New_Offer_Reviewer from './New_Offer_Reviewer';
import { Checkbox_Extra } from '../../../../components/Checkbox_Extra';
import { useHrAccess } from '../../../../services/authentication/HrAccess';
import {
    rich_text_has_content,
    sanitize_rich_text_html
} from '../utils/rich_text_comments';
import {
    creative_fixed_one_off_value,
    creative_service_is_locked,
    marketing_fixed_one_off_value,
    marketing_service_is_locked,
    service_price_value,
    unlocked_payment_mode_values,
    unlocked_service_values
} from '../utils/marketing_service_rules';
import {
    accepted_member_can_manage_agency_preoffer,
    graphic_manager_review_required,
    mandatory_help_review_required,
    offer_catalog_type_key,
    preoffer_coordinator_can_continue,
    preoffer_department_estimate_turn,
    preoffer_department_board_from_types,
    preoffer_growth_estimate_turn,
    reviewer_suggested_pricing_is_complete,
    website_manager_review_required
} from '../utils/offer_creation_rules';

const NewOfferSetup = styled.div`
    display: grid;
    gap: 2rem;
`;

const NewOfferSetupError = styled.p`
    color: var(--error);
`;

const NewOfferSetupReviewHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--normalPads);
`;

const NewOfferSetupServices = styled.div`
    margin-bottom: 1rem;
    display: grid;
    gap: .5rem;
`;

const NewOfferSetupHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 1rem;
    align-items: center;
`;

const NewOfferSetupManual = styled(NewOfferSetupHeader)`
    grid-template-columns: 1fr auto auto auto;
`

const NewOfferSetupCustom = styled.div`
    margin-bottom: 1rem;
`;

const NewOfferSetupAddField = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const NewOfferSetupPriceFlex = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;

    h5,
    h6 {
        margin: 0;
    }
`;

const NewOfferSetupCommercials = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
`;

const NewOfferSetupToggle = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
`;

const NewOfferSetupDeal = styled.div`
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    flex-wrap: wrap;
`;

const NewOfferSetupDealItem = styled.div`
    width: 12rem;
`;

const NewOfferSetupCustomTools = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
`;

const NewOfferSetupContextGrid = styled.div`
    display: grid;
    gap: 0.8rem;
`;

const NewOfferSetupContextRow = styled.div`
    display: grid;
    grid-template-columns: 18rem 1fr;
    gap: 1rem;
    align-items: start;

    h6,
    p {
        margin: 0;
    }
`;

const NewOfferSetupOverviewTools = styled.div`
    margin-top: 0.8rem;
`;

const NewOfferSetupOverviewHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const NewOfferSetupOverviewToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;

    h4 {
        color: ${p => p.$active ? p.theme.color : p.theme.greyDark};
    }
`;

const NewOfferSetupQuestionnaires = styled.div`
    margin-top: 0.8rem;
    padding: 1rem;
    border: 0.1rem dashed ${p => p.theme.mid};
    display: grid;
    gap: 0.8rem;
`;

const NewOfferSetupQuestionOptions = styled.div`
    display: grid;
    gap: 0.6rem;
`;

const NewOfferSetupQuestionOther = styled.div`
    margin-top: 0.4rem;
`;

const NewOfferSetupEshopEstimate = styled.div`
    margin-top: 0.8rem;
    display: grid;
    gap: 0.8rem;
    padding: 1rem;
    border: 0.1rem dashed ${p => p.theme.mid};
`;

const NewOfferSetupReadOnlyHiddenMessage = styled.div`
    border: 0.1rem solid ${p => p.theme.mid};
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    padding: 1rem;
    overflow-wrap: anywhere;

    h6,
    p {
        margin: 0;
    }

    p {
        margin-top: 0.6rem;
        line-height: 1.5;
        white-space: pre-wrap;
    }
`;

const read_type_title = (typeName = '') => {
    if (typeName === 'sass') {
        return 'SASS';
    }

    if (typeName === 'website_creation') {
        return 'Website creation';
    }

    if (typeName === 'website_maintenance') {
        return 'Website maintenance';
    }

    if (typeName === 'website') {
        return 'Website';
    }

    if (typeName === 'marketing') return 'Personalised ads';
    if (typeName === 'graphic' || typeName === 'creative') return 'One-offs';

    return typeName;
};

const NewOfferSetupQuill = styled.div`
    .ql-toolbar {
        border: none;
        border-radius: var(--smallRadius) var(--smallRadius) 0 0;
        background: ${p => p.theme.low};
        box-shadow: ${p => p.theme.out};
    }

    .ql-container {
        min-height: 12rem;
        border: none;
        border-radius: 0 0 var(--smallRadius) var(--smallRadius);
        background: ${p => p.theme.background};
        color: ${p => p.theme.color};
        box-shadow: ${p => p.theme.out};
    }

    .ql-editor {
        min-height: 12rem;
        color: ${p => p.theme.color};
    }

    .ql-editor.ql-blank::before {
        color: ${p => p.theme.high};
        font-style: normal;
    }

    .ql-stroke {
        stroke: ${p => p.theme.color};
    }

    .ql-fill {
        fill: ${p => p.theme.color};
    }
`;

const website_eshop_service_key = 'website_creation_eshop';
const legacy_website_eshop_service_key = 'website_eshop';
const legacy_website_plan_eshop_service_key = 'website_plan_eshop';
const website_plan_starter_service_key = 'website_creation_plan_starter';
const website_plan_business_service_key = 'website_creation_plan_business';
const legacy_website_plan_starter_service_key = 'website_plan_starter';
const legacy_website_plan_business_service_key = 'website_plan_business';
const website_creation_type_key = 'website_creation';

const is_eshop_service_key = (serviceKey = '') => {
    return serviceKey === website_eshop_service_key
        || serviceKey === legacy_website_eshop_service_key
        || serviceKey === legacy_website_plan_eshop_service_key;
};

const is_website_plan_service_key = (serviceKey = '') => {
    return serviceKey.startsWith('website_creation_plan_') || serviceKey.startsWith('website_plan_');
};

const is_website_maintenance_service_key = (serviceKey = '') => {
    return serviceKey.startsWith('website_maintenance_');
};

const is_website_creation_service_key = (serviceKey = '') => {
    return serviceKey.startsWith('website_creation_') || serviceKey.startsWith('website_plan_') || serviceKey === legacy_website_eshop_service_key;
};

const is_website_creation_custom_service_key = (serviceKey = '') => {
    return serviceKey.startsWith('website_creation_custom_');
};

const custom_services_are_enabled_for_type = (typeName = '') => {
    return typeName === website_creation_type_key;
};

const is_starter_plan_service_key = (serviceKey = '') => {
    return serviceKey === website_plan_starter_service_key || serviceKey === legacy_website_plan_starter_service_key;
};

const is_business_plan_service_key = (serviceKey = '') => {
    return serviceKey === website_plan_business_service_key || serviceKey === legacy_website_plan_business_service_key;
};

const is_starter_or_business_plan_service_key = (serviceKey = '') => {
    return is_starter_plan_service_key(serviceKey) || is_business_plan_service_key(serviceKey);
};

const website_questionnaire_type_by_service_key = (serviceKey = '') => {
    if (is_eshop_service_key(serviceKey)) {
        return 'eshop';
    }

    if (is_starter_plan_service_key(serviceKey)) {
        return 'starter';
    }

    if (is_business_plan_service_key(serviceKey)) {
        return 'business';
    }

    return '';
};

const has_website_questionnaire = (serviceKey = '') => {
    return website_questionnaire_type_by_service_key(serviceKey).length > 0;
};

const eshop_option_other_value = 'other';
const website_industry_question = {
    questionKey: 'company_industry',
    questionLabel: 'Σχετικά με την Εταιρεία σας - Κατηγορία (Category)',
    inputType: 'checkbox',
    required: true,
    allowOther: true,
    options: [
        { value: 'retail', label: 'Λιανική (Retail)' },
        { value: 'services', label: 'Υπηρεσίες (Services)' },
        { value: 'hospitality', label: 'Φιλοξενία (Hospitality)' },
        { value: 'technology', label: 'Τεχνολογία (Technology)' },
        { value: 'non_profit', label: 'Μη Κερδοσκοπικός (Non-profit)' },
        { value: eshop_option_other_value, label: 'Άλλο' }
    ]
};

const website_target_question = {
    questionKey: 'target_audience',
    questionLabel: 'Στοχευόμενο κοινό',
    inputType: 'radio',
    required: true,
    options: [
        { value: 'b2b', label: 'B2B' },
        { value: 'b2c', label: 'B2C' },
        { value: 'both', label: 'Και τα δύο (Both)' }
    ]
};

const website_goals_question = {
    questionKey: 'website_goals',
    questionLabel: 'Στόχοι Ιστοσελίδας',
    inputType: 'checkbox',
    required: true,
    allowOther: true,
    options: [
        { value: 'showcase', label: 'Παρουσίαση προϊόντων ή υπηρεσιών (Showcase products or services)' },
        { value: 'leads', label: 'Συλλογή leads (Generate leads)' },
        { value: 'online_sales', label: 'Ηλεκτρονικές πωλήσεις (Online sales - e-commerce)' },
        { value: 'company_information', label: 'Εταιρική παρουσίαση (Share company information)' },
        { value: 'blog_news', label: 'Blog ή νέα (Blog or news updates)' },
        { value: 'customer_support', label: 'Υποστήριξη πελατών / FAQ (Customer support / FAQs)' },
        { value: 'community', label: 'Δημιουργία κοινότητας (Build community)' },
        { value: eshop_option_other_value, label: 'Άλλο' }
    ]
};

const website_basic_features_question = {
    questionKey: 'basic_features',
    questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Βασικά (Basic Features)',
    inputType: 'checkbox',
    required: true,
    allowOther: true,
    options: [
        { value: 'contact_form', label: 'Φόρμα Επικοινωνίας (Contact form)' },
        { value: 'newsletter', label: 'Εγγραφή Newsletter (Newsletter signup)' },
        { value: 'blog_news', label: 'Blog ή νέα (Blog or news updates)' },
        { value: 'image_gallery', label: 'Γκαλερί εικόνων (Image gallery)' },
        { value: 'video_gallery', label: 'Γκαλερί βίντεο (Video gallery)' },
        { value: 'reviews', label: 'Κριτικές (Testimonials / reviews section)' },
        { value: 'search', label: 'Αναζήτηση (Search function)' },
        { value: 'faq', label: 'FAQ (Συχνές Ερωτήσεις & Απαντήσεις)' },
        { value: 'downloadable_files', label: 'Αρχεία για κατέβασμα (Downloadable files)' },
        { value: eshop_option_other_value, label: 'Άλλο' }
    ]
};

const website_advanced_features_question = {
    questionKey: 'advanced_features',
    questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Προχωρημένα (Advanced Features)',
    inputType: 'checkbox',
    required: true,
    options: [
        { value: 'multilingual', label: 'Πολυγλωσσικό (Multilingual website)' },
        { value: 'membership', label: 'Μέλη / Σύνδεση χρήστη (Membership / user login)' },
        { value: 'forum', label: 'Forum ή κοινότητα (Forum / community area)' },
        { value: 'live_chat', label: 'Live chat ή chatbot (Chatbot or live chat)' },
        { value: 'event_calendar', label: 'Ημερολόγιο εκδηλώσεων (Event calendar)' },
        { value: 'interactive_map', label: 'Διαδραστικός χάρτης (Interactive map / store locator)' },
        { value: 'portfolio', label: 'Portfolio' },
        { value: 'third_party_apps', label: 'Σύνδεση με 3rd party apps' },
        { value: 'social_feeds', label: 'Σύνδεση με social media (Social media feeds)' },
        { value: 'custom_forms', label: 'Custom φόρμες (Custom forms / surveys)' },
        { value: 'donation_system', label: 'Σύστημα δωρεών (Donation system)' }
    ]
};

const website_technical_features_question = {
    questionKey: 'technical_features',
    questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Τεχνικά (Technical Features)',
    inputType: 'checkbox',
    required: true,
    options: [
        { value: 'seo', label: 'SEO (Search Engine Optimization)' },
        { value: 'google_analytics', label: 'Google Analytics setup' },
        { value: 'gdpr', label: 'Cookie consent / GDPR' },
        { value: 'accessibility', label: 'Προσβασιμότητα (Accessibility compliance - WCAG)' },
        { value: 'security', label: 'Ασφάλεια (Security: SSL, backups)' },
        { value: 'speed_optimization', label: 'Βελτιστοποίηση ταχύτητας (Speed optimization)' }
    ]
};

const website_has_domain_question = {
    questionKey: 'has_domain',
    questionLabel: 'Έχετε domain; παράδειγμα (www.mywebsite.gr)',
    inputType: 'radio',
    required: true,
    options: [
        { value: 'yes', label: 'Ναι' },
        { value: 'no', label: 'Όχι' }
    ]
};

const website_has_hosting_question = {
    questionKey: 'has_hosting',
    questionLabel: 'Έχετε hosting;',
    inputType: 'radio',
    required: true,
    allowOther: true,
    options: [
        { value: 'yes', label: 'Ναι' },
        { value: 'no', label: 'Όχι' },
        { value: eshop_option_other_value, label: 'Άλλο' }
    ]
};

const eshop_questions = [
    website_industry_question,
    {
        questionKey: 'main_products_services',
        questionLabel: 'Κύρια προϊόντα ή υπηρεσίες',
        inputType: 'textarea',
        required: true
    },
    website_target_question,
    website_goals_question,
    website_basic_features_question,
    website_advanced_features_question,
    website_technical_features_question,
    website_has_domain_question,
    website_has_hosting_question
];

const starter_questions = [
    website_industry_question,
    website_target_question,
    {
        questionKey: 'starter_project_scope',
        questionLabel: 'Περιγράψτε με λίγα λόγια τον σκοπό του website',
        inputType: 'textarea',
        required: true
    },
    website_goals_question,
    {
        questionKey: 'starter_pages',
        questionLabel: 'Ποιες βασικές σελίδες χρειάζεστε;',
        inputType: 'checkbox',
        required: true,
        allowOther: true,
        options: [
            { value: 'home', label: 'Αρχική' },
            { value: 'about', label: 'Σχετικά με εμάς' },
            { value: 'services', label: 'Υπηρεσίες' },
            { value: 'portfolio', label: 'Portfolio' },
            { value: 'contact', label: 'Επικοινωνία' },
            { value: 'blog', label: 'Blog / Νέα' },
            { value: eshop_option_other_value, label: 'Άλλο' }
        ]
    },
    website_has_domain_question,
    website_has_hosting_question
];

const business_questions = [
    website_industry_question,
    {
        questionKey: 'business_products_services',
        questionLabel: 'Κύρια προϊόντα ή υπηρεσίες',
        inputType: 'textarea',
        required: true
    },
    website_target_question,
    website_goals_question,
    website_basic_features_question,
    website_advanced_features_question,
    website_technical_features_question,
    {
        questionKey: 'business_integrations_notes',
        questionLabel: 'Υπάρχουν integrations ή τεχνικές απαιτήσεις που πρέπει να γνωρίζουμε;',
        inputType: 'textarea',
        required: false
    },
    website_has_domain_question,
    website_has_hosting_question
];

const questionnaire_items_by_service_key = (serviceKey = '') => {
    const questionnaireType = website_questionnaire_type_by_service_key(serviceKey);

    if (questionnaireType === 'starter') {
        return starter_questions;
    }

    if (questionnaireType === 'business') {
        return business_questions;
    }

    if (questionnaireType === 'eshop') {
        return eshop_questions;
    }

    return [];
};

const read_normalized_value = (value) => {
    return String(value || '').trim().toLowerCase();
};

const eshop_question_other_key = (questionKey = '') => {
    return `${questionKey}_other`;
};

const checkbox_answer_values = (answers = {}, questionKey = '') => {
    const currentValue = answers?.[questionKey];

    if (Array.isArray(currentValue)) {
        return currentValue;
    }

    if (`${currentValue || ''}`.trim().length) {
        return [`${currentValue}`.trim()];
    }

    return [];
};

const radio_answer_value = (answers = {}, questionKey = '') => {
    return `${answers?.[questionKey] || ''}`.trim();
};

const answer_has_text = (value = '') => {
    return `${value || ''}`.trim().length > 0;
};

const question_has_answer = (answers = {}, questionItem = {}) => {
    const inputType = `${questionItem?.inputType || ''}`.trim().toLowerCase();
    const questionKey = `${questionItem?.questionKey || ''}`.trim();

    if (!questionKey.length) {
        return true;
    }

    if (inputType === 'checkbox') {
        const selectedValues = checkbox_answer_values(answers, questionKey);

        if (!selectedValues.length) {
            return false;
        }

        if (!selectedValues.includes(eshop_option_other_value)) {
            return true;
        }

        return answer_has_text(answers?.[eshop_question_other_key(questionKey)]);
    }

    if (inputType === 'radio') {
        const selectedValue = radio_answer_value(answers, questionKey);

        if (!selectedValue.length) {
            return false;
        }

        if (selectedValue !== eshop_option_other_value) {
            return true;
        }

        return answer_has_text(answers?.[eshop_question_other_key(questionKey)]);
    }

    return answer_has_text(answers?.[questionKey]);
};

const read_can_continue_preoffer_for_department_head = (selectedTypes = [], permissions = {}) => {
    const normalizedTypes = (selectedTypes || []).map((typeItem) => {
        const normalizedType = read_normalized_value(typeItem);

        return normalizedType === 'graphic' ? 'creative' : normalizedType;
    });

    const websiteReviewRequired = website_manager_review_required(normalizedTypes);

    if (websiteReviewRequired) {
        return permissions.isWebsiteDepartmentHead === true;
    }

    if (permissions.isMarketingDepartmentHead && (normalizedTypes.includes('marketing') || normalizedTypes.includes('creative'))) {
        return true;
    }

    if (normalizedTypes.includes('marketing') || normalizedTypes.includes('creative')) {
        return false;
    }

    if (permissions.isCompanyHead) {
        return true;
    }

    return false;
};

const read_member_identity_keys = (memberItem = {}) => {
    return [
        memberItem?.userId,
        memberItem?.subId,
        memberItem?.id,
        memberItem?._id,
        memberItem?.sub,
        memberItem?.email,
        memberItem?.memberEmail,
        memberItem?.username,
        memberItem?.userName,
        memberItem?.memberUsername,
        memberItem?.preferred_username,
        memberItem?.name,
        memberItem?.fullName,
        memberItem?.value,
        memberItem?.label
    ]
        .map((identityValue) => read_normalized_value(identityValue))
        .filter(Boolean);
};

const read_initial_preoffer_enabled = (setupData = {}, typesData = {}) => {
    if (setupData?.preofferEnabled !== undefined) {
        return Boolean(setupData?.preofferEnabled);
    }

    if (typesData?.preofferEnabled === true) {
        return true;
    }

    const requestedAt = `${typesData?.preofferRequestedAt || ''}`.trim();
    const normalizedTurn = read_normalized_value(typesData?.preofferTurn || '');
    const hasTurn = normalizedTurn === 'team_lead'
        || normalizedTurn === 'teamleader'
        || normalizedTurn === 'team_leader'
        || normalizedTurn === 'teamlead'
        || normalizedTurn === 'coordinator'
        || normalizedTurn === 'done';
    const hasOwner = Boolean(typesData?.preofferAssignedTeamLead || typesData?.preofferRequestedBy);

    return requestedAt.length > 0 || hasTurn || hasOwner;
};

const read_is_preoffer_requested = (typesData = {}) => {
    const preofferEnabled = read_initial_preoffer_enabled({}, typesData);
    const requestedAt = `${typesData?.preofferRequestedAt || ''}`.trim();
    const normalizedTurn = read_normalized_value(typesData?.preofferTurn || '');
    const hasTurn = normalizedTurn === 'team_lead'
        || normalizedTurn === 'teamleader'
        || normalizedTurn === 'team_leader'
        || normalizedTurn === 'teamlead'
        || normalizedTurn === 'coordinator'
        || normalizedTurn === 'done';
    const assignedTeamLead = typesData?.preofferAssignedTeamLead;
    const requestedBy = typesData?.preofferRequestedBy;
    const hasOwner = Boolean(assignedTeamLead || requestedBy);

    if (!preofferEnabled) {
        return false;
    }

    if (requestedAt.length > 0) {
        return true;
    }

    return hasTurn && hasOwner;
};

const read_value_or_dash = (value) => {
    const normalizedValue = `${value || ''}`.trim();

    if (!normalizedValue.length) {
        return '—';
    }

    return normalizedValue;
};

const read_offer_language_label = (offerLanguage = '') => {
    const normalizedLanguage = read_normalized_value(offerLanguage);

    if (normalizedLanguage === 'en') {
        return 'English';
    }

    if (normalizedLanguage === 'el') {
        return 'Greek';
    }

    return '—';
};

const has_text = (value = '') => {
    return `${value || ''}`.trim().length > 0;
};

const quill_modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean']
    ]
};

const service_items_from_type = (typeName) => {
    const catalogTypeName = offer_catalog_type_key(typeName);

    return service_catalog_items()
        .filter((service) => service.catalogType === catalogTypeName)
        .map(({ serviceKey, serviceName, serviceValue }) => ({
            serviceKey,
            serviceName,
            serviceText: serviceValue.text,
            serviceRequired: serviceValue.required,
            serviceStatus: serviceValue.status,
            serviceRetired: serviceValue.retired,
            serviceOneOff: serviceValue.one_off ?? false,
            servicePriceDefault: typeof serviceValue.price === 'number' ? serviceValue.price : serviceValue.price?.suggested ?? 15,
            serviceDeal: serviceValue.deal ?? null
        }));
};

const custom_service_item = (customService) => {
    return {
        serviceKey: customService.serviceKey,
        serviceName: 'manual',
        serviceText: customService.text,
        serviceRequired: customService.required,
        serviceStatus: customService.status,
        serviceOneOff: is_website_creation_custom_service_key(customService.serviceKey) ? true : customService.one_off ?? false,
        servicePriceDefault: typeof customService.price === 'number' ? customService.price : 15,
        serviceDeal: customService.deal ?? null
    };
};

const custom_services_for_type = (typeName = '', customServicesByType = {}) => {
    if (!custom_services_are_enabled_for_type(typeName)) {
        return [];
    }

    return (customServicesByType[typeName] || []).map(custom_service_item);
};

const with_visible_notes = (visibilityMap = {}, notesMap = {}) => {
    const noteKeysWithValue = Object.keys(notesMap || {}).filter((serviceKey) => rich_text_has_content(notesMap[serviceKey] || ''));
    const notesVisibilityMap = noteKeysWithValue.reduce((nextMap, serviceKey) => {
        return {
            ...nextMap,
            [serviceKey]: true
        };
    }, {});

    return {
        ...(visibilityMap || {}),
        ...notesVisibilityMap
    };
};

export default function New_Offer_Setup(props) {
    const { t } = useTranslation();
    const { permissions, member } = useHrAccess();
    const isRequestOfferMode = Boolean(props.requestOfferMode);
    const requestOfferSourceMode = Boolean(props.requestOfferSourceMode);
    const requestOfferSubmitMode = props.requestOfferSubmitMode ?? isRequestOfferMode;
    const departmentManagerEstimateMode = ['help_department', 'request_department'].includes(props.offerWorkflowMode);
    const managerReviewReadOnly = departmentManagerEstimateMode && props.managerReviewReadOnly === true;
    const clientEmails = Array.isArray(props.typesData?.clientEmails) ? props.typesData.clientEmails : [];
    const selectedTypesSummary = Array.isArray(props.typesData?.selectedTypes) && props.typesData.selectedTypes.length
        ? props.typesData.selectedTypes.join(', ')
        : Array.isArray(props.selectedTypes) && props.selectedTypes.length
            ? props.selectedTypes.join(', ')
            : '';
    const [selectedServices, setSelectedServices] = useState(props.setupData?.selectedServices || {});
    const [customServicesByType, setCustomServicesByType] = useState(props.setupData?.customServicesByType || {});
    const [customServiceInputByType, setCustomServiceInputByType] = useState({});
    const [serviceComments, setServiceComments] = useState(props.setupData?.serviceComments || {});
    const [serviceHiddenMessages, setServiceHiddenMessages] = useState(props.setupData?.serviceHiddenMessages || {});
    const [showServiceComments, setShowServiceComments] = useState(with_visible_notes(props.setupData?.showServiceComments || {}, props.setupData?.serviceComments || {}));
    const [showHiddenMessages, setShowHiddenMessages] = useState(with_visible_notes(props.setupData?.showHiddenMessages || {}, props.setupData?.serviceHiddenMessages || {}));
    const [serviceOneOffValues, setServiceOneOffValues] = useState(props.setupData?.serviceOneOffValues || {});
    const [servicePriceOverwriteValues, setServicePriceOverwriteValues] = useState(props.setupData?.servicePriceOverwriteValues || {});
    const [serviceDealValues, setServiceDealValues] = useState(props.setupData?.serviceDealValues || {});
    const [serviceAdSpendValues, setServiceAdSpendValues] = useState(props.setupData?.serviceAdSpendValues || {});
    const [serviceWorkhoursValues, setServiceWorkhoursValues] = useState(props.setupData?.serviceWorkhoursValues || {});
    const [serviceSuggestedPricingValues, setServiceSuggestedPricingValues] = useState(props.setupData?.serviceSuggestedPricingValues || {});
    const [eshopQuestionAnswers, setEshopQuestionAnswers] = useState(props.setupData?.eshopQuestionAnswers || {});
    const [starterQuestionAnswers, setStarterQuestionAnswers] = useState(props.setupData?.starterQuestionAnswers || {});
    const [businessQuestionAnswers, setBusinessQuestionAnswers] = useState(props.setupData?.businessQuestionAnswers || {});
    const [eshopProjectText, setEshopProjectText] = useState(props.setupData?.eshopProjectText || '');
    const [brandOverview, setBrandOverview] = useState(props.setupData?.brandOverview ?? props.priceData?.brandOverview ?? '');
    const [noOverview, setNoOverview] = useState(Boolean(props.setupData?.noOverview ?? false));
    const [showOverviewHiddenMessage, setShowOverviewHiddenMessage] = useState(
        Boolean(props.setupData?.showOverviewHiddenMessage ?? props.priceData?.showOverviewHiddenMessage ?? false)
        || has_text(props.setupData?.overviewHiddenMessage ?? props.priceData?.overviewHiddenMessage ?? '')
    );
    const [overviewHiddenMessage, setOverviewHiddenMessage] = useState(
        props.setupData?.overviewHiddenMessage ?? props.priceData?.overviewHiddenMessage ?? ''
    );
    const preofferWorkflowEnabled = props.allowPreoffer !== false;
    const [preofferEnabled, setPreofferEnabled] = useState(
        preofferWorkflowEnabled && read_initial_preoffer_enabled(props.setupData, props.typesData)
    );
    const [setupError, setSetupError] = useState('');
    const [reviewEnabled, setReviewEnabled] = useState(props.setupData?.reviewEnabled === true);
    const [reviewerUserId, setReviewerUserId] = useState(props.setupData?.reviewerUserId || '');
    const [reviewerSelectionMode, setReviewerSelectionMode] = useState(props.setupData?.reviewerSelectionMode || (props.setupData?.reviewerUserId ? 'manual' : 'default'));
    const [reviewerOptions, setReviewerOptions] = useState([]);
    const [currentReviewerUserId, setCurrentReviewerUserId] = useState(member?.userId || member?.subId || member?.id || '');
    const selectedTypes = props.selectedTypes?.length ? props.selectedTypes : [];
    const hasSelectedServices = Object.values(selectedServices).some((value) => value);
    const canManagePreoffer = preofferWorkflowEnabled && accepted_member_can_manage_agency_preoffer(permissions);
    const canSkipWebsiteQuestionnaires = permissions.isGrowthDepartmentMember || permissions.isGrowthDepartmentHead || permissions.isCompanyHead;
    const savedAssignedTeamLead = props.typesData?.preofferAssignedTeamLead || {};
    const assignedTeamLeadIdentityKeys = read_member_identity_keys(savedAssignedTeamLead);
    const currentMemberIdentityKeys = read_member_identity_keys(member || {});
    const isAssignedTeamLead = assignedTeamLeadIdentityKeys.some((identityKey) => currentMemberIdentityKeys.includes(identityKey));
    const isDepartmentHeadPreofferOwner = read_can_continue_preoffer_for_department_head(selectedTypes, permissions);
    const preofferTurn = read_normalized_value(props.typesData?.preofferTurn || 'team_lead');
    const coordinatorIdentityKeys = read_member_identity_keys(props.typesData?.preofferRequestedBy || {});
    const isCoordinatorTurnMember = coordinatorIdentityKeys.some((identityKey) => currentMemberIdentityKeys.includes(identityKey));
    const canContinueCoordinatorPreoffer = preoffer_coordinator_can_continue(permissions, isCoordinatorTurnMember);
    const isPreofferRequested = preofferWorkflowEnabled
        && (read_is_preoffer_requested(props.typesData) || departmentManagerEstimateMode);
    const isTeamLeadEstimateTurn = isPreofferRequested
        && preoffer_department_estimate_turn(props.offerWorkflowMode, preofferTurn)
        && (isAssignedTeamLead || isDepartmentHeadPreofferOwner);
    const canSubmitTeamLeadEstimate = isTeamLeadEstimateTurn && !managerReviewReadOnly;
    const isCoordinatorEstimateTurn = isPreofferRequested
        && preoffer_growth_estimate_turn(props.offerWorkflowMode, preofferTurn)
        && canContinueCoordinatorPreoffer;
    const canContinueRequestOffer = isRequestOfferMode && !requestOfferSubmitMode;
    const canContinueVersionedPreoffer = ['preoffer_v2_preparer', 'preoffer_v2_creator'].includes(props.offerWorkflowMode);
    const versionedPreparerReviewMode = props.offerWorkflowMode === 'preoffer_v2_preparer';
    const reviewerSuggestedPricingMode = isTeamLeadEstimateTurn || versionedPreparerReviewMode;
    const visibleServiceTypes = reviewerSuggestedPricingMode
        ? [...new Set([...selectedTypes.map(offer_catalog_type_key), ...Object.keys(fields).filter((typeName) => {
            return service_items_from_type(typeName).some((service) => selectedServices[service.serviceKey])
                || custom_services_for_type(typeName, customServicesByType).some((service) => selectedServices[service.serviceKey]);
        })])]
        : [...new Set([...selectedTypes, ...service_catalog_items()
            .filter((service) => selectedServices[service.serviceKey])
            .map((service) => service.catalogType)])];
    const isEshopSelected = Object.keys(selectedServices).some((serviceKey) => selectedServices[serviceKey] && is_eshop_service_key(serviceKey));
    const isStarterSelected = Object.keys(selectedServices).some((serviceKey) => selectedServices[serviceKey] && is_starter_plan_service_key(serviceKey));
    const isBusinessSelected = Object.keys(selectedServices).some((serviceKey) => selectedServices[serviceKey] && is_business_plan_service_key(serviceKey));
    const preofferRequiresWebsiteBoard = website_manager_review_required(selectedTypes);
    const preofferRequiresGraphicBoard = graphic_manager_review_required(selectedTypes);
    const preofferRequiresManagerReview = mandatory_help_review_required(selectedTypes, selectedServices);
    const canAskForHelp = !props.askForHelpOnPreview
        && !isRequestOfferMode
        && canManagePreoffer
        && preofferEnabled
        && !isPreofferRequested;
    const service_has_fixed_payment_mode = (serviceKey = '') => {
        return marketing_service_is_locked(serviceKey)
            || creative_service_is_locked(serviceKey)
            || is_website_creation_custom_service_key(serviceKey)
            || is_website_plan_service_key(serviceKey)
            || is_website_maintenance_service_key(serviceKey)
            || is_eshop_service_key(serviceKey);
    };
    const read_fixed_payment_mode = (serviceKey = '') => {
        const marketingFixedValue = marketing_fixed_one_off_value(serviceKey);

        if (marketingFixedValue !== null) {
            return marketingFixedValue;
        }

        const creativeFixedValue = creative_fixed_one_off_value(serviceKey);

        if (creativeFixedValue !== null) {
            return creativeFixedValue;
        }

        if (is_eshop_service_key(serviceKey)) {
            return true;
        }

        if (is_website_creation_custom_service_key(serviceKey)) {
            return true;
        }

        if (is_website_plan_service_key(serviceKey)) {
            return true;
        }

        if (is_website_maintenance_service_key(serviceKey)) {
            return false;
        }

        return null;
    };

    useEffect(() => {
        setSelectedServices(props.setupData?.selectedServices || {});
        setCustomServicesByType(props.setupData?.customServicesByType || {});
        setServiceComments(props.setupData?.serviceComments || {});
        setServiceHiddenMessages(props.setupData?.serviceHiddenMessages || {});
        setShowServiceComments(with_visible_notes(props.setupData?.showServiceComments || {}, props.setupData?.serviceComments || {}));
        setShowHiddenMessages(with_visible_notes(props.setupData?.showHiddenMessages || {}, props.setupData?.serviceHiddenMessages || {}));
        setServiceOneOffValues(props.setupData?.serviceOneOffValues || {});
        setServicePriceOverwriteValues(props.setupData?.servicePriceOverwriteValues || {});
        setServiceDealValues(props.setupData?.serviceDealValues || {});
        setServiceAdSpendValues(props.setupData?.serviceAdSpendValues || {});
        setServiceWorkhoursValues(props.setupData?.serviceWorkhoursValues || {});
        setServiceSuggestedPricingValues(props.setupData?.serviceSuggestedPricingValues || {});
        setEshopQuestionAnswers(props.setupData?.eshopQuestionAnswers || {});
        setStarterQuestionAnswers(props.setupData?.starterQuestionAnswers || {});
        setBusinessQuestionAnswers(props.setupData?.businessQuestionAnswers || {});
        setEshopProjectText(props.setupData?.eshopProjectText || '');
        setBrandOverview(props.setupData?.brandOverview ?? props.priceData?.brandOverview ?? '');
        setNoOverview(Boolean(props.setupData?.noOverview ?? false));
        setShowOverviewHiddenMessage(
            Boolean(props.setupData?.showOverviewHiddenMessage ?? props.priceData?.showOverviewHiddenMessage ?? false)
            || has_text(props.setupData?.overviewHiddenMessage ?? props.priceData?.overviewHiddenMessage ?? '')
        );
        setOverviewHiddenMessage(props.setupData?.overviewHiddenMessage ?? props.priceData?.overviewHiddenMessage ?? '');
        setPreofferEnabled(preofferWorkflowEnabled && read_initial_preoffer_enabled(props.setupData, props.typesData));
        setReviewEnabled(props.setupData?.reviewEnabled === true);
        setReviewerUserId(props.setupData?.reviewerUserId || '');
        setReviewerSelectionMode(props.setupData?.reviewerSelectionMode || (props.setupData?.reviewerUserId ? 'manual' : 'default'));
        setSetupError('');
    }, [props.setupData, props.typesData, props.priceData, preofferWorkflowEnabled]);

    useEffect(() => {
        if (props.allowReview || isRequestOfferMode || !canManagePreoffer || isPreofferRequested || !preofferRequiresManagerReview) {
            return;
        }

        if (!preofferEnabled) {
            setPreofferEnabled(true);
        }
    }, [
        props.allowReview,
        isRequestOfferMode,
        canManagePreoffer,
        isPreofferRequested,
        preofferRequiresManagerReview,
        preofferEnabled
    ]);

    const select_service = (serviceKey, checked) => {
        if (departmentManagerEstimateMode) {
            return;
        }

        setSelectedServices((previousServices) => {
            const nextServices = {
                ...previousServices,
                [serviceKey]: checked
            };

            if (checked && is_website_plan_service_key(serviceKey)) {
                Object.keys(nextServices).forEach((key) => {
                    const isStarterOrBusinessPlan = is_starter_or_business_plan_service_key(key);
                    const selectedIsStarterOrBusinessPlan = is_starter_or_business_plan_service_key(serviceKey);

                    if (selectedIsStarterOrBusinessPlan && isStarterOrBusinessPlan && key !== serviceKey) {
                        nextServices[key] = false;
                    }

                    if (is_website_maintenance_service_key(key)) {
                        nextServices[key] = false;
                    }
                });
            }

            if (checked && is_eshop_service_key(serviceKey)) {
                Object.keys(nextServices).forEach((key) => {
                    if (is_website_maintenance_service_key(key)) {
                        nextServices[key] = false;
                    }
                });
            }

            if (checked && is_website_maintenance_service_key(serviceKey)) {
                Object.keys(nextServices).forEach((key) => {
                    if (is_website_maintenance_service_key(key) && key !== serviceKey) {
                        nextServices[key] = false;
                    }

                    if (is_website_plan_service_key(key)) {
                        nextServices[key] = false;
                    }

                    if (is_eshop_service_key(key)) {
                        nextServices[key] = false;
                    }
                });
            }

            return nextServices;
        });

        if (!checked && is_eshop_service_key(serviceKey)) {
            setEshopProjectText('');
            setEshopQuestionAnswers({});
            setServiceWorkhoursValues((previousWorkhoursValues) => {
                const nextWorkhoursValues = {
                    ...previousWorkhoursValues
                };

                delete nextWorkhoursValues[website_eshop_service_key];
                delete nextWorkhoursValues[legacy_website_eshop_service_key];
                delete nextWorkhoursValues[legacy_website_plan_eshop_service_key];
                return nextWorkhoursValues;
            });
            setServiceSuggestedPricingValues((previousSuggestedPricingValues) => {
                const nextSuggestedPricingValues = {
                    ...previousSuggestedPricingValues
                };

                delete nextSuggestedPricingValues[website_eshop_service_key];
                delete nextSuggestedPricingValues[legacy_website_eshop_service_key];
                delete nextSuggestedPricingValues[legacy_website_plan_eshop_service_key];
                return nextSuggestedPricingValues;
            });
        }

        if (!checked && is_starter_plan_service_key(serviceKey)) {
            setStarterQuestionAnswers({});
        }

        if (!checked && is_business_plan_service_key(serviceKey)) {
            setBusinessQuestionAnswers({});
        }

        setSetupError('');
    };

    const change_review_website_plan = (serviceKey) => {
        if (!reviewerSuggestedPricingMode || managerReviewReadOnly
            || (departmentManagerEstimateMode && !canSubmitTeamLeadEstimate)
            || !is_starter_or_business_plan_service_key(serviceKey)) {
            return;
        }

        setSelectedServices((previousServices) => {
            const nextServices = { ...previousServices };
            Object.keys(nextServices).forEach((key) => {
                if (is_starter_or_business_plan_service_key(key)) {
                    nextServices[key] = false;
                }
            });
            nextServices[serviceKey] = true;
            return nextServices;
        });
        setSetupError('');
    };

    const build_setup_payload = () => {
        const selectedServiceKeys = selected_service_keys();
        const normalizedWorkhoursValues = selectedServiceKeys.reduce((workhoursLookup, serviceKey) => {
            if (serviceWorkhoursValues[serviceKey] === undefined) {
                return workhoursLookup;
            }

            return {
                ...workhoursLookup,
                [serviceKey]: serviceWorkhoursValues[serviceKey]
            };
        }, {});
        const normalizedSuggestedPricingValues = selectedServiceKeys.reduce((pricingLookup, serviceKey) => {
            if (serviceSuggestedPricingValues[serviceKey] === undefined) {
                return pricingLookup;
            }

            return {
                ...pricingLookup,
                [serviceKey]: `${serviceSuggestedPricingValues[serviceKey]}`.trim()
            };
        }, {});
        const preofferAssignedTeamLead = isPreofferRequested && preofferEnabled
            ? props.typesData?.preofferAssignedTeamLead || props.setupData?.preofferAssignedTeamLead || null
            : null;
        const preofferDepartmentBoard = preoffer_department_board_from_types(selectedTypes);

        return {
            selectedServices,
            showServiceComments,
            showHiddenMessages,
            serviceComments,
            serviceHiddenMessages,
            serviceOneOffValues: unlocked_payment_mode_values(serviceOneOffValues),
            servicePriceOverwriteValues: unlocked_service_values(servicePriceOverwriteValues),
            serviceDealValues,
            serviceAdSpendValues,
            serviceWorkhoursValues: normalizedWorkhoursValues,
            serviceSuggestedPricingValues: normalizedSuggestedPricingValues,
            customServicesByType,
            brandOverview: noOverview ? '' : sanitize_rich_text_html(brandOverview || ''),
            noOverview,
            showOverviewHiddenMessage,
            overviewHiddenMessage: `${overviewHiddenMessage || ''}`.trim(),
            eshopQuestionAnswers,
            starterQuestionAnswers,
            businessQuestionAnswers,
            eshopProjectText: sanitize_rich_text_html(eshopProjectText || ''),
            requestOfferMode: requestOfferSourceMode,
            reviewEnabled: props.allowReview ? reviewEnabled : props.setupData?.reviewEnabled,
            reviewerUserId,
            reviewerSelectionMode,
            reviewerIsSelf: reviewerUserId === currentReviewerUserId,
            preofferEnabled: preofferWorkflowEnabled && (props.allowReview ? reviewEnabled : preofferEnabled),
            preofferAssignedTeamLead,
            preofferDepartmentBoard: preofferWorkflowEnabled ? preofferDepartmentBoard : ''
        };
    };

    const has_website_plan_and_maintenance = () => {
        const hasWebsitePlan = Object.keys(selectedServices).some((serviceKey) => {
            return selectedServices[serviceKey] && is_website_creation_service_key(serviceKey);
        });
        const hasWebsiteMaintenance = Object.keys(selectedServices).some((serviceKey) => {
            return selectedServices[serviceKey] && is_website_maintenance_service_key(serviceKey);
        });

        return hasWebsitePlan && hasWebsiteMaintenance;
    };

    const selected_service_keys = () => {
        return Object.keys(selectedServices).filter((serviceKey) => selectedServices[serviceKey]);
    };

    const selected_services_have_workhours = () => {
        const selectedServiceKeys = selected_service_keys();

        if (!selectedServiceKeys.length) {
            return false;
        }

        return !selectedServiceKeys.some((serviceKey) => {
            const currentValue = `${serviceWorkhoursValues[serviceKey] ?? ''}`.trim();
            return !currentValue.length;
        });
    };

    const validate_preoffer_selection = () => {
        if (props.allowReview && reviewEnabled && !reviewerOptions.some((reviewer) => reviewer.userId === reviewerUserId)) {
            setSetupError(t('Select a reviewer before continuing.'));
            return false;
        }

        if (props.allowReview || !preofferWorkflowEnabled) {
            return true;
        }

        if (isRequestOfferMode) {
            return true;
        }

        if (isCoordinatorEstimateTurn) {
            return true;
        }

        if (preofferRequiresManagerReview && !preofferEnabled) {
            const errorMessage = t(preofferRequiresWebsiteBoard
                ? 'Website Manager review is required for website creation and maintenance.'
                : 'Marketing Manager review is required for One-offs offers.');

            setSetupError(errorMessage);
            return false;
        }

        if (!preofferEnabled) {
            return true;
        }

        return true;
    };

    const validate_selected_services = () => {
        if (hasSelectedServices) {
            return true;
        }

        setSetupError(t('Select at least one service before continuing.'));
        return false;
    };

    const go_to_next = () => {
        if (!validate_selected_services()) {
            return;
        }

        if (has_website_plan_and_maintenance()) {
            setSetupError(t('Select website development or website maintenance, not both.'));
            return;
        }

        if (!validate_required_brand_overview()) {
            return;
        }

        if (!validate_required_website_questionnaires()) {
            return;
        }

        if (canAskForHelp) {
            setSetupError(t('Use Ask for help to continue with preoffer.'));
            return;
        }

        if (isPreofferRequested && !isCoordinatorEstimateTurn && !canContinueRequestOffer && !canContinueVersionedPreoffer) {
            setSetupError(t('Preoffer is waiting for the current turn owner.'));
            return;
        }

        if (!validate_preoffer_selection()) {
            return;
        }

        if (props.onNext) {
            props.onNext(build_setup_payload());
        }

        setSetupError('');
    };

    const ask_for_help = () => {
        if (!validate_selected_services()) {
            return;
        }

        if (has_website_plan_and_maintenance()) {
            setSetupError(t('Select website development or website maintenance, not both.'));
            return;
        }

        if (!validate_required_brand_overview()) {
            return;
        }

        if (!validate_required_website_questionnaires()) {
            return;
        }

        if (!validate_preoffer_selection()) {
            return;
        }

        if (!canAskForHelp) {
            setSetupError(t('Preoffer is already in progress.'));
            return;
        }

        if (props.onAskForHelp) {
            props.onAskForHelp(build_setup_payload());
        }

        setSetupError('');
    };

    const change_service_workhours = (serviceKey, value) => {
        setServiceWorkhoursValues((previousWorkhoursValues) => {
            return {
                ...previousWorkhoursValues,
                [serviceKey]: value
            };
        });
    };

    const change_service_suggested_pricing = (serviceKey, value) => {
        setServiceSuggestedPricingValues((previousSuggestedPricingValues) => {
            return {
                ...previousSuggestedPricingValues,
                [serviceKey]: value
            };
        });
    };

    const questionnaire_answers_by_service_key = (serviceKey = '') => {
        if (is_starter_plan_service_key(serviceKey)) {
            return starterQuestionAnswers;
        }

        if (is_business_plan_service_key(serviceKey)) {
            return businessQuestionAnswers;
        }

        if (is_eshop_service_key(serviceKey)) {
            return eshopQuestionAnswers;
        }

        return {};
    };

    const questionnaire_setter_by_service_key = (serviceKey = '') => {
        if (is_starter_plan_service_key(serviceKey)) {
            return setStarterQuestionAnswers;
        }

        if (is_business_plan_service_key(serviceKey)) {
            return setBusinessQuestionAnswers;
        }

        if (is_eshop_service_key(serviceKey)) {
            return setEshopQuestionAnswers;
        }

        return null;
    };

    const change_question_answer = (serviceKey, questionKey, value) => {
        const setQuestionAnswers = questionnaire_setter_by_service_key(serviceKey);

        if (!setQuestionAnswers) {
            return;
        }

        setQuestionAnswers((previousAnswers) => {
            return {
                ...previousAnswers,
                [questionKey]: value
            };
        });
    };

    const toggle_question_checkbox = (serviceKey, questionKey, optionValue, checked) => {
        const setQuestionAnswers = questionnaire_setter_by_service_key(serviceKey);

        if (!setQuestionAnswers) {
            return;
        }

        setQuestionAnswers((previousAnswers) => {
            const selectedValues = checkbox_answer_values(previousAnswers, questionKey);
            const nextValues = checked
                ? [...new Set([...selectedValues, optionValue])]
                : selectedValues.filter((valueItem) => valueItem !== optionValue);
            const nextAnswers = {
                ...previousAnswers,
                [questionKey]: nextValues
            };

            if (!checked && optionValue === eshop_option_other_value) {
                delete nextAnswers[eshop_question_other_key(questionKey)];
            }

            return nextAnswers;
        });
    };

    const change_question_other = (serviceKey, questionKey, value) => {
        const setQuestionAnswers = questionnaire_setter_by_service_key(serviceKey);

        if (!setQuestionAnswers) {
            return;
        }

        setQuestionAnswers((previousAnswers) => {
            return {
                ...previousAnswers,
                [eshop_question_other_key(questionKey)]: value
            };
        });
    };

    const change_question_radio = (serviceKey, questionKey, value) => {
        const setQuestionAnswers = questionnaire_setter_by_service_key(serviceKey);

        if (!setQuestionAnswers) {
            return;
        }

        setQuestionAnswers((previousAnswers) => {
            const nextAnswers = {
                ...previousAnswers,
                [questionKey]: value
            };

            if (value !== eshop_option_other_value) {
                delete nextAnswers[eshop_question_other_key(questionKey)];
            }

            return nextAnswers;
        });
    };

    const change_eshop_project_text = (value) => {
        setEshopProjectText(sanitize_rich_text_html(value || ''));
    };

    const toggle_no_overview = () => {
        setNoOverview((currentValue) => !currentValue);
    };

    const missing_questionnaire_items_for_service = (serviceKey = '') => {
        const questionItems = questionnaire_items_by_service_key(serviceKey);
        const questionAnswers = questionnaire_answers_by_service_key(serviceKey);

        return questionItems.filter((questionItem) => {
            if (questionItem?.required === false) {
                return false;
            }

            return !question_has_answer(questionAnswers, questionItem);
        });
    };

    const has_missing_required_questionnaires = () => {
        const selectedServiceKeys = selected_service_keys();
        const websiteQuestionnaireServiceKeys = selectedServiceKeys.filter((serviceKey) => has_website_questionnaire(serviceKey));

        return websiteQuestionnaireServiceKeys.some((serviceKey) => {
            return missing_questionnaire_items_for_service(serviceKey).length > 0;
        });
    };

    const validate_required_brand_overview = () => {
        if (noOverview) {
            return true;
        }

        if (rich_text_has_content(brandOverview || '')) {
            return true;
        }

        setSetupError(t('Overview is required when enabled.'));
        return false;
    };

    const validate_required_website_questionnaires = () => {
        if (canSkipWebsiteQuestionnaires) {
            return true;
        }

        if (!has_missing_required_questionnaires()) {
            return true;
        }

        setSetupError(t('Fill all required website questionnaire fields before continuing.'));
        return false;
    };

    const submit_estimate = () => {
        if (!validate_selected_services()) {
            return;
        }

        if (!canSubmitTeamLeadEstimate) {
            setSetupError(t('Estimate can be submitted only on Department Head turn.'));
            return;
        }

        if (isEshopSelected) {
            const normalizedProjectText = sanitize_rich_text_html(eshopProjectText || '');

            if (!rich_text_has_content(normalizedProjectText)) {
                setSetupError(t('Fill Project for eShop.'));
                return;
            }
        }

        if (!reviewer_suggested_pricing_is_complete(selectedServices, serviceSuggestedPricingValues)) {
            setSetupError(t('Fill a positive suggested price for every selected service.'));
            return;
        }

        if (props.onSubmitEstimate) {
            props.onSubmitEstimate(build_setup_payload());
        }

        setSetupError('');
    };

    const request_offer = () => {
        if (!isRequestOfferMode || !requestOfferSubmitMode) {
            return;
        }

        if (!validate_selected_services()) {
            return;
        }

        if (!validate_required_brand_overview()) {
            return;
        }

        if (!validate_required_website_questionnaires()) {
            return;
        }

        if (!selected_services_have_workhours()) {
            setSetupError(t('Fill workhours for every selected service.'));
            return;
        }

        if (props.onRequestOffer) {
            props.onRequestOffer(build_setup_payload());
        }

        setSetupError('');
    };

    const change_custom_service_input = (typeName, value) => {
        setCustomServiceInputByType((previousInputByType) => {
            return {
                ...previousInputByType,
                [typeName]: value
            };
        });
    };

    const clear_service_state_by_key = (serviceKey) => {
        setSelectedServices((previousServices) => {
            const nextServices = { ...previousServices };
            delete nextServices[serviceKey];
            return nextServices;
        });
        setShowServiceComments((previousComments) => {
            const nextComments = { ...previousComments };
            delete nextComments[serviceKey];
            return nextComments;
        });
        setShowHiddenMessages((previousHiddenMessages) => {
            const nextHiddenMessages = { ...previousHiddenMessages };
            delete nextHiddenMessages[serviceKey];
            return nextHiddenMessages;
        });
        setServiceComments((previousServiceComments) => {
            const nextServiceComments = { ...previousServiceComments };
            delete nextServiceComments[serviceKey];
            return nextServiceComments;
        });
        setServiceHiddenMessages((previousServiceHiddenMessages) => {
            const nextServiceHiddenMessages = { ...previousServiceHiddenMessages };
            delete nextServiceHiddenMessages[serviceKey];
            return nextServiceHiddenMessages;
        });
        setServiceOneOffValues((previousOneOffValues) => {
            const nextOneOffValues = { ...previousOneOffValues };
            delete nextOneOffValues[serviceKey];
            return nextOneOffValues;
        });
        setServicePriceOverwriteValues((previousPriceValues) => {
            const nextPriceValues = { ...previousPriceValues };
            delete nextPriceValues[serviceKey];
            return nextPriceValues;
        });
        setServiceDealValues((previousDealValues) => {
            const nextDealValues = { ...previousDealValues };
            delete nextDealValues[serviceKey];
            return nextDealValues;
        });
        setServiceAdSpendValues((previousAdSpendValues) => {
            const nextAdSpendValues = { ...previousAdSpendValues };
            delete nextAdSpendValues[serviceKey];
            return nextAdSpendValues;
        });
    };

    const ads_spend_defaults = {
        meta_ads: 150,
        google_ads: 300,
        tiktok_ads: 600
    };

    const read_service_adspend_default = (serviceKey) => {
        const normalizedServiceKey = `${serviceKey || ''}`.trim().toLowerCase();

        if (normalizedServiceKey.endsWith('_meta_ads')) {
            return ads_spend_defaults.meta_ads;
        }

        if (normalizedServiceKey.endsWith('_google_ads')) {
            return ads_spend_defaults.google_ads;
        }

        if (normalizedServiceKey.endsWith('_tiktok_ads')) {
            return ads_spend_defaults.tiktok_ads;
        }

        return null;
    };

    const read_service_adspend_value = (serviceKey) => {
        const savedValue = serviceAdSpendValues[serviceKey];
        const defaultValue = read_service_adspend_default(serviceKey);

        if (savedValue === '' || savedValue === 0 || savedValue) {
            return savedValue;
        }

        return defaultValue;
    };

    const service_uses_adspend = (serviceKey) => {
        return read_service_adspend_default(serviceKey) !== null;
    };

    const change_service_adspend = (serviceKey, value) => {
        setSelectedServices((previousServices) => {
            return {
                ...previousServices,
                [serviceKey]: true
            };
        });
        setServiceAdSpendValues((previousAdSpendValues) => {
            return {
                ...previousAdSpendValues,
                [serviceKey]: value
            };
        });
    };

    const add_custom_service = (typeName, textValue) => {
        const customText = (textValue || customServiceInputByType[typeName] || '').trim();

        if (!customText) {
            return;
        }

        const customKey = `${typeName}_custom_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

        setCustomServicesByType((previousServicesByType) => {
            return {
                ...previousServicesByType,
                [typeName]: [
                    ...(previousServicesByType[typeName] || []),
                    {
                        serviceKey: customKey,
                        serviceName: 'manual',
                        status: true,
                        text: customText,
                        comments: '',
                        required: null,
                        one_off: typeName === website_creation_type_key,
                        price: 15,
                        deal: null
                    }
                ]
            };
        });

        setCustomServiceInputByType((previousInputByType) => {
            return {
                ...previousInputByType,
                [typeName]: ''
            };
        });
    };

    const add_custom_service_field = (typeName) => {
        const nextNumber = (customServicesByType[typeName] || []).length + 1;
        add_custom_service(typeName, `Manual service ${nextNumber}`);
    };

    const remove_custom_service = (typeName, serviceIndex) => {
        const customServices = customServicesByType[typeName] || [];
        const removedService = customServices[serviceIndex];

        setCustomServicesByType((previousServicesByType) => {
            return {
                ...previousServicesByType,
                [typeName]: (previousServicesByType[typeName] || []).filter((_, index) => index !== serviceIndex)
            };
        });

        if (removedService?.serviceKey) {
            clear_service_state_by_key(removedService.serviceKey);
        }
    };

    const remove_custom_service_by_key = (typeName, serviceKey) => {
        const serviceIndex = (customServicesByType[typeName] || []).findIndex((service) => service.serviceKey === serviceKey);

        if (serviceIndex === -1) {
            return;
        }

        remove_custom_service(typeName, serviceIndex);
    };

    const change_custom_service_name = (typeName, serviceKey, value) => {
        setCustomServicesByType((previousServicesByType) => {
            return {
                ...previousServicesByType,
                [typeName]: (previousServicesByType[typeName] || []).map((service) => {
                    if (service.serviceKey === serviceKey) {
                        return {
                            ...service,
                            text: value
                        };
                    }

                    return service;
                })
            };
        });
    };

    const change_custom_service_price = (typeName, serviceKey, value) => {
        const nextPrice = value === '' ? '' : Number(value);

        setCustomServicesByType((previousServicesByType) => {
            return {
                ...previousServicesByType,
                [typeName]: (previousServicesByType[typeName] || []).map((service) => {
                    if (service.serviceKey === serviceKey) {
                        return {
                            ...service,
                            price: nextPrice
                        };
                    }

                    return service;
                })
            };
        });
    };

    const custom_service_name_by_key = (typeName, serviceKey) => {
        const serviceItem = (customServicesByType[typeName] || []).find((service) => service.serviceKey === serviceKey);
        return serviceItem?.text || '';
    };

    const custom_service_price_by_key = (typeName, serviceKey) => {
        const serviceItem = (customServicesByType[typeName] || []).find((service) => service.serviceKey === serviceKey);
        return serviceItem?.price ?? 15;
    };

    const toggle_service_comments = (serviceKey) => {
        setSelectedServices((previousServices) => {
            return {
                ...previousServices,
                [serviceKey]: true
            };
        });
        setShowServiceComments((previousComments) => {
            return {
                ...previousComments,
                [serviceKey]: !previousComments[serviceKey]
            };
        });
    };

    const toggle_hidden_messages = (serviceKey) => {
        setSelectedServices((previousServices) => {
            return {
                ...previousServices,
                [serviceKey]: true
            };
        });
        setShowHiddenMessages((previousHiddenMessages) => {
            return {
                ...previousHiddenMessages,
                [serviceKey]: !previousHiddenMessages[serviceKey]
            };
        });
    };

    const change_service_comment = (serviceKey, value) => {
        setServiceComments((previousComments) => {
            return {
                ...previousComments,
                [serviceKey]: value
            };
        });
    };

    const change_service_hidden_message = (serviceKey, value) => {
        setServiceHiddenMessages((previousHiddenMessages) => {
            return {
                ...previousHiddenMessages,
                [serviceKey]: value
            };
        });
    };

    const toggle_service_one_off = (serviceKey, currentValue) => {
        if (service_has_fixed_payment_mode(serviceKey)) {
            return;
        }

        setSelectedServices((previousServices) => {
            return {
                ...previousServices,
                [serviceKey]: true
            };
        });
        setServiceOneOffValues((previousOneOffValues) => {
            return {
                ...previousOneOffValues,
                [serviceKey]: !currentValue
            };
        });
    };

    const change_service_price_overwrite = (serviceKey, value) => {
        if (marketing_service_is_locked(serviceKey)) {
            return;
        }

        setServicePriceOverwriteValues((previousPriceValues) => {
            return {
                ...previousPriceValues,
                [serviceKey]: value
            };
        });
    };

    const change_service_deal_value = (service, fieldName, value) => {
        setServiceDealValues((previousDealValues) => {
            const defaultDeal = {
                stories: service.serviceDeal?.stories ?? 0,
                reel: service.serviceDeal?.reel ?? 0,
                static: service.serviceDeal?.static ?? 0
            };

            return {
                ...previousDealValues,
                [service.serviceKey]: {
                    ...(previousDealValues[service.serviceKey] || defaultDeal),
                    [fieldName]: value
                }
            };
        });
    };
    const read_service_one_off_value = (serviceItem) => {
        const fixedPaymentMode = read_fixed_payment_mode(serviceItem.serviceKey);

        if (fixedPaymentMode === null) {
            return serviceOneOffValues[serviceItem.serviceKey] ?? serviceItem.serviceOneOff;
        }

        return fixedPaymentMode;
    };

    return (
        <NewOfferSetup>
            {managerReviewReadOnly ? (
                <p>{t('This legacy review was already submitted. Review the saved details, then complete the Cerebro Preoffer manually.')}</p>
            ) : null}
            {props.showClientContext ? (
                <FormBox>
                    <FormBoxTitle>
                        <h4>{t('Client context')}</h4>
                    </FormBoxTitle>
                    <NewOfferSetupContextGrid>
                        <NewOfferSetupContextRow>
                            <h6>{t('Offer code')}</h6>
                            <p>{read_value_or_dash(props.typesData?.offerCode)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Types')}</h6>
                            <p>{read_value_or_dash(selectedTypesSummary)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Emails')}</h6>
                            <p>{read_value_or_dash(clientEmails.join(', '))}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Category')}</h6>
                            <p>{read_value_or_dash(props.typesData?.category || props.typesData?.sourceClientCategory || props.typesData?.industry)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Subcategory')}</h6>
                            <p>{read_value_or_dash(props.typesData?.subcategory || props.typesData?.sourceClientSubcategory || props.typesData?.Subcategory)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Brand or full name')}</h6>
                            <p>{read_value_or_dash(props.typesData?.clientName)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Headquarters')}</h6>
                            <p>{read_value_or_dash(props.typesData?.clientHeadquarters)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Language')}</h6>
                            <p>{read_offer_language_label(props.typesData?.offerLanguage)}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('Invoice')}</h6>
                            <p>{props.typesData?.invoiceEnabled === false ? t('Disabled') : t('Enabled')}</p>
                        </NewOfferSetupContextRow>
                        <NewOfferSetupContextRow>
                            <h6>{t('VAT number')}</h6>
                            <p>{read_value_or_dash(props.typesData?.clientVatNumber)}</p>
                        </NewOfferSetupContextRow>
                    </NewOfferSetupContextGrid>
                </FormBox>
            ) : null}
            <FormBox>
                <FormBoxTitle>
                    <NewOfferSetupOverviewHeader>
                        <NewOfferSetupOverviewToggle $active={!noOverview}>
                            <h4>{t('Overview')}</h4>
                            <Toggle_
                                active={!noOverview}
                                onToggle={toggle_no_overview}
                                disabled={departmentManagerEstimateMode}
                            />
                        </NewOfferSetupOverviewToggle>
                    </NewOfferSetupOverviewHeader>
                </FormBoxTitle>
                {!noOverview ? (
                    <>
                        <NewOfferSetupQuill>
                            <ReactQuill
                                theme="snow"
                                modules={quill_modules}
                                placeholder={t('Add an introduction overview for this offer')}
                                value={brandOverview}
                                onChange={(value) => setBrandOverview(sanitize_rich_text_html(value || ''))}
                                readOnly={departmentManagerEstimateMode}
                            />
                        </NewOfferSetupQuill>
                        {!departmentManagerEstimateMode ? <NewOfferSetupOverviewTools>
                            <Grey_Link
                                iconLeft="EyeClose"
                                text={t('Hidden message')}
                                onClick={() => setShowOverviewHiddenMessage(!showOverviewHiddenMessage)}
                            />
                        </NewOfferSetupOverviewTools> : null}
                        {showOverviewHiddenMessage || has_text(overviewHiddenMessage) ? (
                            departmentManagerEstimateMode ? (
                                has_text(overviewHiddenMessage) ? (
                                    <NewOfferSetupReadOnlyHiddenMessage>
                                        <h6>{t('Hidden message')}</h6>
                                        <p>{overviewHiddenMessage}</p>
                                    </NewOfferSetupReadOnlyHiddenMessage>
                                ) : null
                            ) : (
                                <Textarea_
                                    placeholder={t('Hidden message')}
                                    value={overviewHiddenMessage}
                                    onChange={(event) => setOverviewHiddenMessage(event.target.value)}
                                    rows="3"
                                />
                            )
                        ) : null}
                    </>
                ) : null}
            </FormBox>
            {props.allowReview ? (
                <FormBox>
                    <FormBoxTitle>
                        <NewOfferSetupReviewHeader>
                            <h4 id="offer-review-label">{t('Review offer')}</h4>
                            <div
                                role="switch"
                                tabIndex={0}
                                aria-labelledby="offer-review-label"
                                aria-checked={reviewEnabled}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setReviewEnabled(!reviewEnabled);
                                        setSetupError('');
                                    }
                                }}
                            >
                                <Toggle_
                                    active={reviewEnabled}
                                    onToggle={() => {
                                        setReviewEnabled(!reviewEnabled);
                                        setSetupError('');
                                    }}
                                ></Toggle_>
                            </div>
                        </NewOfferSetupReviewHeader>
                    </FormBoxTitle>
                    <p>{reviewEnabled
                        ? t('Open a pre-offer issue for the selected reviewer, then return the offer to you for creation.')
                        : t('Create this offer directly without review.')}</p>
                </FormBox>
            ) : null}
            {props.allowReview && reviewEnabled ? (
                <New_Offer_Reviewer
                    offerType={offer_catalog_type_key(selectedTypes[0])}
                    value={reviewerUserId}
                    currentUserId={member?.userId || member?.subId || member?.id || ''}
                    selectionMode={reviewerSelectionMode}
                    onChange={(value, mode = 'manual') => {
                        setReviewerUserId(value);
                        setReviewerSelectionMode(mode);
                    }}
                    onOptions={(options, currentUserId) => {
                        setReviewerOptions(options);
                        if (currentUserId) setCurrentReviewerUserId(currentUserId);
                    }}
                ></New_Offer_Reviewer>
            ) : null}
            {visibleServiceTypes.map((typeName) => {
                const servicesForType = [
                    ...service_items_from_type(typeName),
                    ...custom_services_for_type(typeName, customServicesByType)
                ].filter((serviceItem) => {
                    if (serviceItem.serviceRetired && !selectedServices[serviceItem.serviceKey]) {
                        return false;
                    }

                    return (!departmentManagerEstimateMode && selectedTypes.some((selectedType) => offer_catalog_type_key(selectedType) === offer_catalog_type_key(typeName))) || selectedServices[serviceItem.serviceKey];
                });

                return (
                    <FormBox key={typeName}>
                        <FormBoxTitle>
                            <h4>{t(`${read_type_title(typeName)} services`)}</h4>
                        </FormBoxTitle>
                        {typeName === website_creation_type_key && reviewerSuggestedPricingMode && (isStarterSelected || isBusinessSelected) ? (
                            <Dropdown_ label={t('Website creation plan')}>
                                <select
                                    aria-label={t('Website creation plan')}
                                    value={isBusinessSelected ? website_plan_business_service_key : website_plan_starter_service_key}
                                    disabled={managerReviewReadOnly || (departmentManagerEstimateMode && !canSubmitTeamLeadEstimate)}
                                    onChange={(event) => change_review_website_plan(event.target.value)}
                                >
                                    <option value={website_plan_starter_service_key}>{t('Starter')}</option>
                                    <option value={website_plan_business_service_key}>{t('Business')}</option>
                                </select>
                            </Dropdown_>
                        ) : null}
                        {servicesForType.map((service) => (
                            <NewOfferSetupServices key={service.serviceKey}>
                                <NewOfferSetupManual>
                                    <Checkbox_Extra
                                        title={service.serviceText}
                                        text={service.serviceRequired}
                                        checked={selectedServices[service.serviceKey] || false}
                                        disabled={!service.serviceStatus || departmentManagerEstimateMode}
                                        onChange={(checked) => select_service(service.serviceKey, checked)}
                                    />
                                    {!departmentManagerEstimateMode ? (
                                        <Main_Plain
                                            iconLeft="Edit"
                                            onClick={() => toggle_service_comments(service.serviceKey)}
                                            noPadding
                                        />
                                    ) : null}
                                    {!departmentManagerEstimateMode ? <Grey_Link
                                        iconLeft="EyeClose"
                                        onClick={() => toggle_hidden_messages(service.serviceKey)}
                                        noPadding
                                    /> : null}
                                    {service.serviceName === 'manual' && !departmentManagerEstimateMode ? (
                                        <Red_
                                            iconLeft="Delete"
                                            onClick={() => remove_custom_service_by_key(typeName, service.serviceKey)}
                                        />
                                    ) : null}
                                </NewOfferSetupManual>
                                {service.serviceName === 'manual' ? (
                                    <NewOfferSetupCustomTools>
                                        <Input_
                                            placeholder={t('Field name')}
                                            value={custom_service_name_by_key(typeName, service.serviceKey)}
                                            onChange={(event) => change_custom_service_name(typeName, service.serviceKey, event.target.value)}
                                            disabled={departmentManagerEstimateMode}
                                        />
                                        {!reviewerSuggestedPricingMode && !isRequestOfferMode ? (
                                            <>
                                                <Input_
                                                    placeholder={t('Price')}
                                                    type="number"
                                                    value={custom_service_price_by_key(typeName, service.serviceKey)}
                                                    onChange={(event) => change_custom_service_price(typeName, service.serviceKey, event.target.value)}
                                                />
                                                <h6>{t('One-off fixed')}</h6>
                                            </>
                                        ) : null}
                                    </NewOfferSetupCustomTools>
                                ) : null}
                                {selectedServices[service.serviceKey] && (service.serviceName !== 'manual' || reviewerSuggestedPricingMode || isRequestOfferMode) ? (
                                    <NewOfferSetupCommercials>
                                        {reviewerSuggestedPricingMode ? (
                                            <NewOfferSetupPriceFlex>
                                                <h6>{`${t('Offer service price')}: € ${Number(service_price_value(service.serviceKey, service.servicePriceDefault, servicePriceOverwriteValues) || 0).toFixed(2)}`}</h6>
                                                <Input_
                                                    placeholder={t('Suggested pricing (€)')}
                                                    type="number"
                                                    min="0.01"
                                                    step="0.01"
                                                    value={serviceSuggestedPricingValues[service.serviceKey] ?? ''}
                                                    onChange={(event) => change_service_suggested_pricing(service.serviceKey, event.target.value)}
                                                    disabled={managerReviewReadOnly}
                                                />
                                            </NewOfferSetupPriceFlex>
                                        ) : isRequestOfferMode ? (
                                            <NewOfferSetupPriceFlex>
                                                <Input_
                                                    placeholder={t('Workhours')}
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={serviceWorkhoursValues[service.serviceKey] ?? ''}
                                                    onChange={(event) => change_service_workhours(service.serviceKey, event.target.value)}
                                                    disabled={managerReviewReadOnly}
                                                />
                                            </NewOfferSetupPriceFlex>
                                        ) : (
                                            <>
                                                <NewOfferSetupPriceFlex>
                                                    {marketing_service_is_locked(service.serviceKey) ? (
                                                        <h6>{`€ ${service.servicePriceDefault}`}</h6>
                                                    ) : (
                                                        <Input_
                                                            placeholder={t('Price')}
                                                            type="number"
                                                            value={service_price_value(service.serviceKey, service.servicePriceDefault, servicePriceOverwriteValues)}
                                                            onChange={(event) => change_service_price_overwrite(service.serviceKey, event.target.value)}
                                                        />
                                                    )}
                                                    {service_uses_adspend(service.serviceKey) ? (
                                                        <Input_
                                                            placeholder={t('Adspend')}
                                                            type="number"
                                                            value={read_service_adspend_value(service.serviceKey)}
                                                            onChange={(event) => change_service_adspend(service.serviceKey, event.target.value)}
                                                        />
                                                    ) : null}
                                                </NewOfferSetupPriceFlex>
                                                {service.serviceDeal ? (
                                                    <NewOfferSetupDeal>
                                                        <NewOfferSetupDealItem>
                                                            <Input_
                                                                placeholder={t('Stories')}
                                                                type="number"
                                                                value={serviceDealValues[service.serviceKey]?.stories ?? service.serviceDeal.stories}
                                                                onChange={(event) => change_service_deal_value(service, 'stories', event.target.value)}
                                                            />
                                                        </NewOfferSetupDealItem>
                                                        <NewOfferSetupDealItem>
                                                            <Input_
                                                                placeholder={t('Reels')}
                                                                type="number"
                                                                value={serviceDealValues[service.serviceKey]?.reel ?? service.serviceDeal.reel}
                                                                onChange={(event) => change_service_deal_value(service, 'reel', event.target.value)}
                                                            />
                                                        </NewOfferSetupDealItem>
                                                        <NewOfferSetupDealItem>
                                                            <Input_
                                                                placeholder={t('Static')}
                                                                type="number"
                                                                value={serviceDealValues[service.serviceKey]?.static ?? service.serviceDeal.static}
                                                                onChange={(event) => change_service_deal_value(service, 'static', event.target.value)}
                                                            />
                                                        </NewOfferSetupDealItem>
                                                    </NewOfferSetupDeal>
                                                ) : null}
                                                {service_has_fixed_payment_mode(service.serviceKey) ? (
                                                    <h6>{read_service_one_off_value(service) ? t('One-off fixed') : t('Recurring fixed')}</h6>
                                                ) : (
                                                    <NewOfferSetupToggle>
                                                        <h6>{t('Recurring')}</h6>
                                                        <Toggle_
                                                            active={read_service_one_off_value(service)}
                                                            onToggle={() => toggle_service_one_off(service.serviceKey, read_service_one_off_value(service))}
                                                        />
                                                        <h6>{t('one-off')}</h6>
                                                    </NewOfferSetupToggle>
                                                )}
                                            </>
                                        )}
                                    </NewOfferSetupCommercials>
                                ) : null}
                                {selectedServices[service.serviceKey] && has_website_questionnaire(service.serviceKey) ? (
                                    <NewOfferSetupQuestionnaires>
                                        {questionnaire_items_by_service_key(service.serviceKey).map((questionItem) => {
                                            const inputType = `${questionItem?.inputType || 'textarea'}`.trim().toLowerCase();
                                            const serviceQuestionAnswers = questionnaire_answers_by_service_key(service.serviceKey);
                                            const selectedCheckboxValues = inputType === 'checkbox'
                                                ? checkbox_answer_values(serviceQuestionAnswers, questionItem.questionKey)
                                                : [];
                                            const selectedRadioValue = inputType === 'radio'
                                                ? radio_answer_value(serviceQuestionAnswers, questionItem.questionKey)
                                                : '';
                                            const showOtherInput = Boolean(questionItem?.allowOther) && (
                                                (inputType === 'checkbox' && selectedCheckboxValues.includes(eshop_option_other_value))
                                                || (inputType === 'radio' && selectedRadioValue === eshop_option_other_value)
                                            );

                                            return (
                                                <Builder_ key={`${service.serviceKey}_${questionItem.questionKey}`} title={questionItem.questionLabel}>
                                                {inputType === 'textarea' ? (
                                                    <Textarea_
                                                        placeholder={questionItem.questionLabel}
                                                        value={serviceQuestionAnswers[questionItem.questionKey] || ''}
                                                        onChange={(event) => change_question_answer(service.serviceKey, questionItem.questionKey, event.target.value)}
                                                        rows="2"
                                                        disabled={managerReviewReadOnly}
                                                    />
                                                ) : null}
                                                {inputType === 'checkbox' ? (
                                                    <NewOfferSetupQuestionOptions>
                                                        {(questionItem.options || []).map((optionItem) => (
                                                            <Checkbox_
                                                                key={`${service.serviceKey}_${questionItem.questionKey}_${optionItem.value}`}
                                                                id={`${service.serviceKey}_${questionItem.questionKey}_${optionItem.value}`}
                                                                text={optionItem.label}
                                                                checked={selectedCheckboxValues.includes(optionItem.value)}
                                                                onChange={(event) => toggle_question_checkbox(service.serviceKey, questionItem.questionKey, optionItem.value, event.target.checked)}
                                                                disabled={managerReviewReadOnly}
                                                            />
                                                        ))}
                                                    </NewOfferSetupQuestionOptions>
                                                ) : null}
                                                {inputType === 'radio' ? (
                                                    <NewOfferSetupQuestionOptions>
                                                        {(questionItem.options || []).map((optionItem) => (
                                                            <Radio_
                                                                key={`${service.serviceKey}_${questionItem.questionKey}_${optionItem.value}`}
                                                                id={`${service.serviceKey}_${questionItem.questionKey}_${optionItem.value}`}
                                                                name={`${service.serviceKey}_${questionItem.questionKey}`}
                                                                text={optionItem.label}
                                                                checked={selectedRadioValue === optionItem.value}
                                                                value={optionItem.value}
                                                                onChange={(event) => {
                                                                    if (event.target.checked) {
                                                                        change_question_radio(service.serviceKey, questionItem.questionKey, optionItem.value);
                                                                    }
                                                                }}
                                                                disabled={managerReviewReadOnly}
                                                            />
                                                        ))}
                                                    </NewOfferSetupQuestionOptions>
                                                ) : null}
                                                {showOtherInput ? (
                                                    <NewOfferSetupQuestionOther>
                                                        <Input_
                                                            placeholder={t('Άλλο')}
                                                            value={serviceQuestionAnswers[eshop_question_other_key(questionItem.questionKey)] || ''}
                                                            onChange={(event) => change_question_other(service.serviceKey, questionItem.questionKey, event.target.value)}
                                                            disabled={managerReviewReadOnly}
                                                        />
                                                    </NewOfferSetupQuestionOther>
                                                ) : null}
                                            </Builder_>
                                            );
                                        })}
                                    {(isTeamLeadEstimateTurn || isCoordinatorEstimateTurn || versionedPreparerReviewMode) && is_eshop_service_key(service.serviceKey) ? (
                                        <NewOfferSetupEshopEstimate>
                                            <h6>{t('Project')}</h6>
                                            <NewOfferSetupQuill>
                                                <ReactQuill
                                                    theme="snow"
                                                    modules={quill_modules}
                                                    placeholder={t('Project')}
                                                    value={eshopProjectText}
                                                    onChange={change_eshop_project_text}
                                                    readOnly={managerReviewReadOnly}
                                                />
                                            </NewOfferSetupQuill>
                                        </NewOfferSetupEshopEstimate>
                                    ) : null}
                                    </NewOfferSetupQuestionnaires>
                                ) : null}
                                {selectedServices[service.serviceKey] && !departmentManagerEstimateMode && (showServiceComments[service.serviceKey] || rich_text_has_content(serviceComments[service.serviceKey])) ? (
                                    <Textarea_
                                        placeholder={t('Comments')}
                                        value={serviceComments[service.serviceKey] || ''}
                                        onChange={(event) => change_service_comment(service.serviceKey, event.target.value)}
                                        rows="3"
                                    />
                                ) : null}
                                {selectedServices[service.serviceKey] && (showHiddenMessages[service.serviceKey] || has_text(serviceHiddenMessages[service.serviceKey])) ? (
                                    departmentManagerEstimateMode ? (
                                        has_text(serviceHiddenMessages[service.serviceKey]) ? (
                                            <NewOfferSetupReadOnlyHiddenMessage>
                                                <h6>{t('Hidden message')}</h6>
                                                <p>{serviceHiddenMessages[service.serviceKey]}</p>
                                            </NewOfferSetupReadOnlyHiddenMessage>
                                        ) : null
                                    ) : (
                                        <Textarea_
                                            placeholder={t('Hidden message')}
                                            value={serviceHiddenMessages[service.serviceKey] || ''}
                                            onChange={(event) => change_service_hidden_message(service.serviceKey, event.target.value)}
                                            rows="3"
                                        />
                                    )
                                ) : null}
                            </NewOfferSetupServices>
                        ))}
                        {custom_services_are_enabled_for_type(typeName) && !departmentManagerEstimateMode ? (
                            <Main_Plain
                                text={t('Add services')}
                                iconLeft="Add"
                                onClick={() => add_custom_service_field(typeName)}
                            />
                        ) : null}
                    </FormBox>
                );
            })}
            {setupError.length ? <NewOfferSetupError role="alert">{setupError}</NewOfferSetupError> : null}
            <Actions_>
                <Grey_
                    text={t('Back')}
                    onClick={props.onBack}
                    iconLeft="Back"
                />
                {canAskForHelp ? (
                    <Main_
                        text={t('Ask for help')}
                        iconRight="Next"
                        onClick={ask_for_help}
                    />
                ) : null}
                {canSubmitTeamLeadEstimate ? (
                    <Main_
                        text={preofferRequiresWebsiteBoard
                            ? t('Complete Website Manager review')
                            : preofferRequiresGraphicBoard ? t('Complete Marketing Manager review') : t('Submit estimate')}
                        iconRight="Next"
                        onClick={submit_estimate}
                    />
                ) : null}
                {isRequestOfferMode && requestOfferSubmitMode ? (
                    <Main_
                        text={t('Submit')}
                        iconRight="Next"
                        onClick={request_offer}
                    />
                ) : null}
                {(!isRequestOfferMode || canContinueRequestOffer) && !canAskForHelp && !isTeamLeadEstimateTurn && (!isPreofferRequested || isCoordinatorEstimateTurn || canContinueRequestOffer || canContinueVersionedPreoffer) ? (
                    <Main_
                        text={t('Next')}
                        iconRight="Next"
                        onClick={go_to_next}
                    />
                ) : null}
            </Actions_>
        </NewOfferSetup>
    );
};
